/**
 * Employer account + roles store.
 *
 * Employers post a role through /post-job. That creates their account and
 * issues a username + password (emailed to them once the backend is live).
 * They log in at /login and manage everything from /employer.
 * Persisted in localStorage until a real backend is wired in.
 */

import type { VoiceTake } from "./showcase";

export type EmployerJob = {
  id: string;
  /** Employer facing reference number, searchable in the dashboard. */
  ref: string;
  title: string;
  seniority: string;
  contract: string;
  company: string;
  anonymous: boolean;
  location: string;
  setting: string;
  payStructure: string;
  pay: string;
  advert: string;
  tags: string[];
  status: "Live" | "Paused" | "Closed";
  createdAt: string;
  /**
   * Admin created role. Never listed on the public job board: candidates only
   * reach it through a direct invitation from the CLOSDD team.
   */
  exclusive?: boolean;
  /** Admin approval decision. Present when sourced from the API. */
  decision?: "Pending" | "Approved" | "Rejected";
  /** Why Closdd rejected the role — shown to the employer until they fix and resubmit it. */
  decisionReason?: string | null;
  /** Flagged as demo/sample data — usually from a CSV import — so admins can spot and clear it later. */
  demo?: boolean;
  /** Pinned to the top of Find jobs with a Featured tag. Set by an admin when approving. */
  featured?: boolean;
  /** Kept off the public job board even while live and approved. */
  hidden?: boolean;
  /** Has the employer paid to unlock this role's candidates. Present when sourced from the API. */
  unlocked?: boolean;
  /** When they paid. Present when sourced from the API. */
  paidAt?: string | null;
};

export type EmployerAccount = {
  company: string;
  contactName: string;
  email: string;
  phone: string;
  username: string;
  password: string;
  createdAt: string;
};

/**
 * Account invitation for roles the CLOSDD team posted on the employer's behalf.
 * Admin presses "Send account access" and the employer receives an email with a
 * link to their welcome page at /welcome/<ref>.
 */
export type EmployerInvite = {
  ref: string;
  email: string;
  contactName: string;
  company: string;
  sentAt: string;
};

export type Application = {
  id: string;
  jobId: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  years: string;
  cvName: string;
  /** The real, downloadable file. Present when sourced from the API. */
  cvUrl?: string;
  hasPitch: boolean;
  /** The real, playable recording. Present when sourced from the API — see AddAudioUrlToApplicationsTable. */
  audioUrl?: string;
  voiceAnswers: number;
  /** The candidate's 5 recorded assessment answers, keyed by question id (q1..q5). Present when sourced from the employer applications API. */
  assessment?: Record<string, VoiceTake>;
  /** Salary expectation band the candidate picked on their profile. */
  salaryExpectation?: string;
  /** The candidate's public profile (/profile/{slug}), when they have one. */
  profileSlug?: string | null;
  status: "New" | "Shortlisted" | "Rejected" | "Interviewing" | "Hired";
  appliedAt: string;
  /** Admin approval decision. Present when sourced from the API. */
  decision?: "Pending" | "Approved" | "Rejected";
};

export type EmployerStore = {
  account: EmployerAccount | null;
  jobs: EmployerJob[];
  applications: Application[];
  loggedIn: boolean;
  /**
   * Free accounts see teasers only: first name and the pitch audio.
   * Upgrading unlocks contact details, CV downloads and voice assessments.
   */
  plan?: "free" | "pro";
  /**
   * True when CLOSDD created this account for the employer (we posted the role
   * and sourced the candidates). Sourced employers land on the gated console at
   * /employer-preview instead of the full /employer dashboard.
   */
  sourced?: boolean;
  /** The access invite the admin team sent out, if any. */
  invite?: EmployerInvite | null;
  /**
   * Candidate access, tracked per role. Every role starts with a 3 day free
   * candidate preview (first names, experience and every 60 second pitch).
   * Full names, CVs and contact details unlock when the employer pays
   * $149 for that specific role.
   */
  access?: Record<string, RoleAccess>;
};

/** Per role candidate access record. The $149 unlock applies to one role, never the account. */
export type RoleAccess = {
  jobId: string;
  /** ISO date the free candidate preview started. */
  previewStart: string;
  /** ISO date the free candidate preview ends. */
  previewEnd: string;
  /** True once the employer has paid $149 for this role. */
  unlocked: boolean;
  paidAt?: string | null;
  amount?: number;
  /** Candidate application ids the employer has opened. */
  viewed?: string[];
};

export const PREVIEW_DAYS = 3;
export const UNLOCK_PRICE = 149;
const DAY = 24 * 60 * 60 * 1000;

function makeAccess(jobId: string, start = new Date()): RoleAccess {
  return {
    jobId,
    previewStart: start.toISOString(),
    previewEnd: new Date(start.getTime() + PREVIEW_DAYS * DAY).toISOString(),
    unlocked: false,
    paidAt: null,
    viewed: [],
  };
}

export function accessFor(store: EmployerStore, jobId: string): RoleAccess {
  return store.access?.[jobId] ?? makeAccess(jobId);
}

/**
 * Starts the free candidate preview clock for any role that does not have one
 * yet. Called when the employer opens their console.
 */
export function ensureRoleAccess(jobIds: string[], kind?: EmployerKind): EmployerStore {
  const store = loadEmployer(kind);
  const access = { ...(store.access ?? {}) };
  let changed = false;
  jobIds.forEach((id) => {
    if (!access[id]) {
      access[id] = makeAccess(id);
      changed = true;
    }
  });
  return changed ? saveEmployer({ access }, kind) : store;
}

export type RoleAccessState = {
  unlocked: boolean;
  previewActive: boolean;
  daysLeft: number;
  previewStart: string;
  previewEnd: string;
  /** "Candidates unlocked" or the locked message. */
  label: string;
};

export function roleAccessState(store: EmployerStore, jobId: string, now = Date.now()): RoleAccessState {
  const record = accessFor(store, jobId);
  // Access is strictly per role: only a paid unlock on this role opens it.
  const unlocked = record.unlocked;
  const end = new Date(record.previewEnd).getTime();
  const msLeft = end - now;
  const previewActive = !unlocked && msLeft > 0;
  const daysLeft = Math.max(0, Math.ceil(msLeft / DAY));
  return {
    unlocked,
    previewActive,
    daysLeft,
    previewStart: record.previewStart,
    previewEnd: record.previewEnd,
    label: unlocked ? "Candidates unlocked" : "Locked — unlock to view full profiles",
  };
}


/** True when full names, CVs and contact details are open for this role. */
export function isRoleUnlocked(store: EmployerStore, jobId: string, now = Date.now()) {
  return roleAccessState(store, jobId, now).unlocked;
}

/** Applies the $149 payment to one role only. */
export function unlockRole(jobId: string, kind?: EmployerKind): EmployerStore {
  const store = loadEmployer(kind);
  const current = accessFor(store, jobId);
  return saveEmployer(
    {
      access: {
        ...(store.access ?? {}),
        [jobId]: { ...current, unlocked: true, paidAt: new Date().toISOString(), amount: UNLOCK_PRICE },
      },
    },
    kind,
  );
}

/** Records that the employer opened a candidate, for admin reporting. */
export function markCandidateViewed(jobId: string, appId: string, kind?: EmployerKind): EmployerStore {
  const store = loadEmployer(kind);
  const current = accessFor(store, jobId);
  const viewed = current.viewed ?? [];
  if (viewed.includes(appId)) return store;
  return saveEmployer(
    { access: { ...(store.access ?? {}), [jobId]: { ...current, viewed: [...viewed, appId] } } },
    kind,
  );
}

/** Admin control: push the preview end date out by a number of days. */
export function extendPreview(jobId: string, days: number, kind?: EmployerKind): EmployerStore {
  const store = loadEmployer(kind);
  const current = accessFor(store, jobId);
  const from = Math.max(Date.now(), new Date(current.previewEnd).getTime());
  return saveEmployer(
    {
      access: {
        ...(store.access ?? {}),
        [jobId]: { ...current, previewEnd: new Date(from + days * DAY).toISOString() },
      },
    },
    kind,
  );
}

/** Admin control: end the free preview right now. */
export function endPreview(jobId: string, kind?: EmployerKind): EmployerStore {
  const store = loadEmployer(kind);
  const current = accessFor(store, jobId);
  return saveEmployer(
    { access: { ...(store.access ?? {}), [jobId]: { ...current, previewEnd: new Date().toISOString() } } },
    kind,
  );
}

/** Admin control: relock a role, for refunds or mistakes. */
export function relockRole(jobId: string, kind?: EmployerKind): EmployerStore {
  const store = loadEmployer(kind);
  const current = accessFor(store, jobId);
  return saveEmployer(
    { access: { ...(store.access ?? {}), [jobId]: { ...current, unlocked: false, paidAt: null } } },
    kind,
  );
}

/**
 * Two independent employer workspaces live side by side on the same device:
 * - "direct": the employer posted their own role through /post-job.
 * - "sourced": CLOSDD created the account and sourced the candidates.
 */
export type EmployerKind = "direct" | "sourced";

const LEGACY_KEY = "closdd.employer";
const KEYS: Record<EmployerKind, string> = {
  direct: "closdd.employer.direct",
  sourced: "closdd.employer.sourced",
};
const ACTIVE_KEY = "closdd.employer.active";

export const emptyStore: EmployerStore = {
  account: null,
  jobs: [],
  applications: [],
  loggedIn: false,
  plan: "free",
  sourced: false,
  invite: null,
  access: {},
};

/** True when the employer has paid to unlock full applications. */
export function isPro(store: EmployerStore) {
  return store.plan === "pro";
}

/** Teaser name: employers see first names only until they upgrade. */
export function firstNameOf(name: string) {
  return name.split(" ").filter(Boolean)[0] ?? "Candidate";
}

export function maskEmail(email: string) {
  const [user = "", domain = ""] = email.split("@");
  if (!domain) return "Hidden until you upgrade";
  return `${user.slice(0, 2)}${"•".repeat(Math.max(3, user.length - 2))}@${domain.replace(/^[^.]*/, "•••")}`;
}

export function maskPhone(phone: string) {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return "Hidden until you upgrade";
  return `${phone.slice(0, 4)} ••• ••${digits.slice(-1)}`;
}

/** Which workspace the person on this device is currently using. */
export function loadEmployerKind(): EmployerKind {
  if (typeof window === "undefined") return "direct";
  return window.localStorage.getItem(ACTIVE_KEY) === "sourced" ? "sourced" : "direct";
}

export function setActiveEmployerKind(kind: EmployerKind) {
  if (typeof window !== "undefined") window.localStorage.setItem(ACTIVE_KEY, kind);
}

/** Moves any pre split single account into the workspace it belongs to. */
function migrateLegacy() {
  if (typeof window === "undefined") return;
  const raw = window.localStorage.getItem(LEGACY_KEY);
  if (!raw) return;
  try {
    const old = JSON.parse(raw) as Partial<EmployerStore>;
    const kind: EmployerKind = old.sourced ? "sourced" : "direct";
    if (!window.localStorage.getItem(KEYS[kind])) {
      window.localStorage.setItem(KEYS[kind], JSON.stringify({ ...old, sourced: kind === "sourced" }));
      setActiveEmployerKind(kind);
    }
  } catch {
    /* ignore malformed legacy data */
  }
  window.localStorage.removeItem(LEGACY_KEY);
}

export function loadEmployer(kind?: EmployerKind): EmployerStore {
  if (typeof window === "undefined") return emptyStore;
  migrateLegacy();
  const k = kind ?? loadEmployerKind();
  try {
    const raw = window.localStorage.getItem(KEYS[k]);
    if (!raw) return { ...emptyStore, sourced: k === "sourced" };
    return { ...emptyStore, ...(JSON.parse(raw) as Partial<EmployerStore>), sourced: k === "sourced" };
  } catch {
    return { ...emptyStore, sourced: k === "sourced" };
  }
}

export function saveEmployer(patch: Partial<EmployerStore>, kind?: EmployerKind): EmployerStore {
  const k = kind ?? loadEmployerKind();
  const next = { ...loadEmployer(k), ...patch, sourced: k === "sourced" };
  if (typeof window !== "undefined") window.localStorage.setItem(KEYS[k], JSON.stringify(next));
  return next;
}

/** Every live role across both workspaces, for the public job board and apply flow. */
export function allEmployerJobs(): EmployerJob[] {
  return [...loadEmployer("direct").jobs, ...loadEmployer("sourced").jobs];
}

/** Ready made demo logins so both employer journeys can be reviewed side by side. */
export const DEMO_EMPLOYERS: Record<EmployerKind, EmployerAccount> = {
  direct: {
    company: "Northgate Systems",
    contactName: "Sarah Whitfield",
    email: "sarah@northgate-demo.com",
    phone: "+44 7700 900101",
    username: "demo-direct",
    password: "direct-1234",
    createdAt: "Today",
  },
  sourced: {
    company: "Brightpath Software",
    contactName: "Daniel Okafor",
    email: "daniel@brightpath-demo.com",
    phone: "+44 7700 900202",
    username: "demo-sourced",
    password: "sourced-1234",
    createdAt: "Today",
  },
};

/** Creates both demo employer workspaces if they do not exist yet. */
export function ensureDemoEmployers() {
  if (typeof window === "undefined") return;
  (["direct", "sourced"] as EmployerKind[]).forEach((kind) => {
    const store = loadEmployer(kind);
    if (store.account) return;
    const demo = makeDemoData();
    const account = DEMO_EMPLOYERS[kind];
    saveEmployer(
      {
        account,
        jobs: demo.jobs.map((j) => ({ ...j, company: account.company })),
        applications: demo.applications,
        plan: "free",
        loggedIn: false,
      },
      kind,
    );
  });
}

const words = ["close", "pipeline", "quota", "target", "deal", "pitch"];

export function makeCredentials(company: string, email: string) {
  const base =
    (company || email.split("@")[0] || "employer")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 18) || "employer";
  const n = Math.floor(100 + Math.random() * 900);
  const word = words[Math.floor(Math.random() * words.length)];
  return {
    username: `${base}-${n}`,
    password: `${word}-${Math.random().toString(36).slice(2, 6)}-${n}`,
  };
}

export function slugId(title: string, company: string) {
  const s = `${title}-${company}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${s || "role"}-${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Auto reference like AS-1846 when the field is left blank: the role's
 * initials, then four digits. Mirrors api/app/Libraries/JobRef.php.
 */
export function makeRef(title = "") {
  const t = title.toLowerCase();
  const hints: [string, string][] = [
    ["appointment set", "AS"],
    ["closer", "HTC"],
    ["business development", "BDM"],
    ["account executive", "AE"],
    ["account manager", "AM"],
    ["customer s", "CS"],
    ["sales manager", "SM"],
    ["recruitment", "RC"],
    ["fundrais", "FR"],
    ["sdr", "SDR"],
    ["bdr", "SDR"],
    ["sales executive", "SE"],
  ];
  const stop = new Set(["a", "an", "and", "or", "of", "the", "for", "to", "in", "remote", "senior", "junior"]);
  const initials = t
    .split(/[^a-z]+/)
    .filter((w) => w && !stop.has(w))
    .slice(0, 3)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const prefix = hints.find(([k]) => t.includes(k))?.[1] ?? (initials.length >= 2 ? initials : "SE");
  return `${prefix}-${1000 + Math.floor(Math.random() * 9000)}`;
}

/** Demo applications so employers can see the review flow before the backend lands. */
export function seedApplications(jobId: string): Application[] {
  const people = [
    { name: "Jordan Miles", email: "jordan.miles@email.com", phone: "+44 7700 900123", location: "London, UK", years: "4 years in sales", voiceAnswers: 3, status: "New" as const, appliedAt: "2 hours ago" },
    { name: "Amara Bello", email: "amara.bello@email.com", phone: "+44 7700 900456", location: "Manchester, UK", years: "2 years in sales", voiceAnswers: 2, status: "New" as const, appliedAt: "Yesterday" },
    { name: "Ryan Doyle", email: "ryan.doyle@email.com", phone: "+44 7700 900789", location: "Remote, UK", years: "6 years in sales", voiceAnswers: 3, status: "Shortlisted" as const, appliedAt: "3 days ago" },
  ];
  return people.map((p, i) => ({
    id: `${jobId}-app-${i}`,
    jobId,
    cvName: `${p.name.split(" ")[0]?.toLowerCase()}-cv.pdf`,
    hasPitch: true,
    ...p,
  }));
}

export function initialsOf(name: string) {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

/**
 * Demo roles + applications so a brand new employer account has something to
 * look at before the backend is live.
 */
export function makeDemoData(): { jobs: EmployerJob[]; applications: Application[] } {
  // Unique per workspace: a shared id/ref made invitations resolve to the wrong employer.
  const unique = Math.random().toString(36).slice(2, 6);
  const job: EmployerJob = {
    id: `demo-role-${unique}`,
    ref: `CL-${unique.toUpperCase()}`,
    title: "Inside Sales Executive",
    seniority: "Mid level",
    contract: "Full time",
    company: "Your company",
    anonymous: false,
    location: "Manchester, UK",
    setting: "Hybrid",
    payStructure: "Base plus commission",
    pay: "£32,000 base, £55,000 OTE",
    advert:
      "Outbound and inbound B2B sales into mid market accounts. You own the full cycle from first call to close, with warm inbound support and a tight product demo.",
    tags: ["B2B", "Commission", "Hybrid"],
    status: "Live",
    createdAt: "Today",
  };

  const people = [
    { name: "Jordan Mills", email: "jordan.mills@email.com", phone: "+44 7700 900123", location: "Manchester, UK", years: "4 years in sales", voiceAnswers: 3, appliedAt: "2 hours ago", status: "New" as const },
    { name: "Priya Shah", email: "priya.shah@email.com", phone: "+44 7700 900456", location: "Salford, UK", years: "6 years in sales", voiceAnswers: 3, appliedAt: "Yesterday", status: "New" as const },
    { name: "Callum Wright", email: "callum.wright@email.com", phone: "+44 7700 900789", location: "Stockport, UK", years: "2 years in sales", voiceAnswers: 2, appliedAt: "Yesterday", status: "New" as const },
    { name: "Amara Okoye", email: "amara.okoye@email.com", phone: "+44 7700 900222", location: "Manchester, UK", years: "5 years in sales", voiceAnswers: 3, appliedAt: "2 days ago", status: "Shortlisted" as const },
    { name: "Tom Fenwick", email: "tom.fenwick@email.com", phone: "+44 7700 900333", location: "Leeds, UK", years: "3 years in sales", voiceAnswers: 1, appliedAt: "3 days ago", status: "New" as const },
    { name: "Bea Lindqvist", email: "bea.lindqvist@email.com", phone: "+44 7700 900444", location: "Manchester, UK", years: "7 years in sales", voiceAnswers: 3, appliedAt: "4 days ago", status: "New" as const },
  ];

  const applications: Application[] = people.map((p, i) => ({
    id: `${job.id}-app-${i}`,
    jobId: job.id,
    cvName: `${p.name.split(" ")[0]?.toLowerCase()}-cv.pdf`,
    hasPitch: true,
    ...p,
  }));

  return { jobs: [job], applications };
}


/**
 * Matches the built in demo logins. These always work, even if a real account
 * is already stored in that workspace, so both journeys can be reviewed.
 */
export function matchDemoEmployer(identifier: string, password: string): EmployerKind | null {
  const id = identifier.trim().toLowerCase();
  const pw = password.trim();
  const kinds: EmployerKind[] = ["direct", "sourced"];
  return (
    kinds.find((kind) => {
      const d = DEMO_EMPLOYERS[kind];
      return (id === d.username || id === d.email.toLowerCase()) && pw === d.password;
    }) ?? null
  );
}

/** Logs into a demo workspace, filling it with demo roles if it is empty. */
export function signInDemoEmployer(kind: EmployerKind): EmployerStore {
  const store = loadEmployer(kind);
  const account = DEMO_EMPLOYERS[kind];
  const demo = store.jobs.length === 0 ? makeDemoData() : null;
  setActiveEmployerKind(kind);
  return saveEmployer(
    {
      account,
      loggedIn: true,
      plan: store.plan ?? "free",
      ...(demo
        ? {
            jobs: demo.jobs.map((j) => ({ ...j, company: account.company })),
            applications: demo.applications,
          }
        : {}),
    },
    kind,
  );
}
